<template>
    <div class="edit-content" v-if="activeClient">
        <!-- Id (readonly) -->
        <div class="edit-row" v-if="activeClient.id">
            <span class="row-title">Id</span>
            <span class="row-value-readonly">{{ activeClient.id }}</span>
        </div>

        <!-- Name, Chain name, Shop name -->
        <div class="edit-row">
            <span class="row-title">{{ "Name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'name')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Chain name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.chain_name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'chain_name')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.shop_name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_name')"
            />
        </div>

        <!-- Business type, Shop type -->
        <div class="edit-row">
            <span class="row-title">{{ "Business type" | t }}</span>
            <v-select
                :value="activeClient.business_type"
                :items="businessTypeOptions"
                item-text="text"
                item-value="value"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'business_type')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop type" | t }}</span>
            <v-text-field
                :value="activeClient.shop_type"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_type')"
            />
        </div>

        <!-- Comment, Address fields -->
        <div class="edit-row">
            <span class="row-title">{{ "Comment" | t }}</span>
            <v-text-field
                :value="activeClient.comment"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'comment')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop address" | t }}</span>
            <v-text-field
                :value="activeClient.shop_address"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_address')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop place" | t }}</span>
            <v-text-field
                :value="activeClient.shop_place"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_place')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop parish" | t }}</span>
            <v-text-field
                :value="activeClient.shop_parish"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_parish')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop county" | t }}</span>
            <v-text-field
                :value="activeClient.shop_county"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_county')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop index" | t }}</span>
            <v-text-field
                :value="activeClient.shop_index"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_index')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop country" | t }}</span>
            <v-text-field
                :value="activeClient.shop_country"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_country')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop country code" | t }}</span>
            <v-autocomplete
                v-model="shopCountry"
                :items="countries"
                class="compact-input"
                dense
                hide-details
                hide-no-data
                item-text="display_name"
                item-value="code"
                outlined
                :placeholder="t('Start typing to Search')"
                return-object
                clearable
            />
        </div>

        <!-- Latitude, Longitude -->
        <div class="edit-row">
            <span class="row-title">{{ "Latitude" | t }}</span>
            <v-text-field
                :value="activeClient.latitude"
                class="compact-input"
                dense
                hide-details
                outlined
                type="number"
                @input="updateField($event, 'latitude')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Longitude" | t }}</span>
            <v-text-field
                :value="activeClient.longitude"
                class="compact-input"
                dense
                hide-details
                outlined
                type="number"
                @input="updateField($event, 'longitude')"
            />
        </div>

        <!-- Client country, Price list, Contractual relationship -->
        <div class="edit-row">
            <span class="row-title">{{ "Client country" | t }} <span class="required">*</span></span>
            <v-autocomplete
                v-model="country"
                :items="countries"
                :loading="isLoading"
                class="compact-input"
                dense
                hide-details
                hide-no-data
                item-text="display_name"
                item-value="code"
                outlined
                :placeholder="t('Start typing to Search')"
                return-object
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Price list" | t }}</span>
            <v-text-field
                :value="activeClient.price_list"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'price_list')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Contractual relationship" | t }}</span>
            <v-text-field
                :value="activeClient.contractual_relationship"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'contractual_relationship')"
            />
        </div>

        <!-- Export info (export clients only) -->
        <template v-if="isExportClient">
            <div class="edit-row">
                <span class="row-title">{{ "Region" | t }}</span>
                <v-select
                    :value="activeClient.region"
                    :items="regionOptions"
                    item-text="text"
                    item-value="value"
                    class="compact-input"
                    dense
                    hide-details
                    outlined
                    clearable
                    @input="updateField($event, 'region')"
                />
            </div>
            <div class="edit-row">
                <span class="row-title">{{ "Met place" | t }}</span>
                <v-combobox
                    v-model="metPlace"
                    :items="metPlaces"
                    item-text="name"
                    item-value="name"
                    :return-object="false"
                    class="compact-input"
                    dense
                    hide-details
                    outlined
                    clearable
                    :placeholder="t('Start typing to Search')"
                />
            </div>
            <div class="edit-row">
                <span class="row-title">{{ "Met date" | t }}</span>
                <div class="compact-input date-picker-wrap">
                    <date-picker v-model="metDate" :popover="{ placement: 'bottom', visibility: 'click' }" />
                </div>
            </div>
            <div class="edit-row">
                <span class="row-title">{{ "Total profit" | t }}</span>
                <v-text-field
                    :value="activeClient.total_profit"
                    class="compact-input"
                    dense
                    hide-details
                    outlined
                    type="number"
                    @input="updateField($event, 'total_profit')"
                />
            </div>

            <!-- Contacts -->
            <div class="edit-row align-start">
                <span class="row-title">{{ "Contacts" | t }}</span>
                <div class="contacts-wrap">
                    <v-card
                        v-for="(contact, index) in (activeClient.contacts || [])"
                        :key="index"
                        outlined
                        class="pa-3 mb-2"
                    >
                        <v-text-field
                            :value="contact.name"
                            :label="'Name' | t"
                            dense outlined hide-details class="compact-input mb-2"
                            @input="updateContact(index, 'name', $event)"
                        />
                        <v-text-field
                            :value="contact.position"
                            :label="'Position' | t"
                            dense outlined hide-details class="compact-input mb-2"
                            @input="updateContact(index, 'position', $event)"
                        />
                        <v-text-field
                            :value="contact.phone"
                            :label="'Phone' | t"
                            dense outlined hide-details class="compact-input mb-2"
                            @input="updateContact(index, 'phone', $event)"
                        />
                        <v-text-field
                            :value="contact.email"
                            :label="'Email' | t"
                            dense outlined hide-details class="compact-input mb-2"
                            @input="updateContact(index, 'email', $event)"
                        />
                        <v-text-field
                            :value="contact.comment"
                            :label="'Comment' | t"
                            dense outlined hide-details class="compact-input mb-2"
                            @input="updateContact(index, 'comment', $event)"
                        />
                        <v-btn text small color="red" @click="removeContact(index)">
                            <v-icon left small>delete</v-icon>
                            {{ "Delete" | t }}
                        </v-btn>
                    </v-card>
                    <v-btn text small color="bal-red" @click="addContact">
                        <v-icon left small>add</v-icon>
                        {{ "Add contact" | t }}
                    </v-btn>
                </div>
            </div>
        </template>

        <!-- Tags -->
        <div class="edit-row">
            <span class="row-title">{{ "Tags" | t }}</span>
            <v-autocomplete
                v-model="selectedTagIds"
                :items="tags"
                item-text="name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                dense
                outlined
                hide-details
                class="compact-input"
                :placeholder="t('Select tags')"
            />
        </div>
    </div>
</template>

<script>
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import moment from "moment";
import Store from "@/store";
import { mapState } from "vuex";

export default {
    name: "client-info-edit-tab",
    components: {
        DatePicker
    },
    data() {
        return {
            country: {},
            shopCountry: null,
            isLoading: true,
            metDate: "",
            metPlace: null,
            businessTypeOptions: [
                { text: "Export", value: "export" },
                { text: "Eesti", value: "eesti" }
            ],
            regionOptions: [
                { text: "-", value: "none" },
                { text: "Europe", value: "europe" },
                { text: "Northern USA", value: "northern_usa" },
                { text: "Southern USA", value: "southern_usa" },
                { text: "Asia", value: "asia" },
                { text: "Oceania", value: "oceania" },
                { text: "Africa", value: "africa" },
                { text: "Middle East", value: "middle_east" }
            ]
        };
    },
    async mounted() {
        if (this.countries.length === 0) {
            await Store.dispatch("fetchCountries");
        }
        if (!this.metPlaces || this.metPlaces.length === 0) {
            Store.dispatch("ClientStore/getAllMetPlaces");
        }

        setTimeout(() => {
            if (this.activeClient && this.activeClient.country_code) {
                this.country = this.countries.find(x => x.code === this.activeClient.country_code);
            }
            if (this.activeClient && this.activeClient.shop_country_code) {
                this.shopCountry = this.countries.find(x => x.code === this.activeClient.shop_country_code);
            }
            if (this.activeClient && this.activeClient.met_date) {
                this.metDate = moment(this.activeClient.met_date, "DD.MM.YYYY").toDate();
            }
            if (this.activeClient && this.activeClient.met_place_name) {
                this.metPlace = this.activeClient.met_place_name;
            }
            this.isLoading = false;
        }, 200);
    },
    watch: {
        country: function(value) {
            value && value.code
                ? this.updateField(value.code, "country")
                : this.updateField(null, "country");
        },
        shopCountry: function(value) {
            value && value.code
                ? this.updateField(value.code, "shop_country_code")
                : this.updateField(null, "shop_country_code");
        },
        metDate: function(value) {
            this.updateField(value ? moment(value).format("DD.MM.YYYY") : null, "met_date");
        },
        metPlace: function(value) {
            // v-combobox uses :return-object="false", so value is always a string (or null)
            this.updateField(value || null, "met_place_name");
        }
    },
    computed: {
        ...mapState(["tags", "countries"]),
        ...mapState("ClientStore", ["activeClient", "metPlaces"]),
        isExportClient() {
            return this.activeClient.business_type === "export";
        },
        selectedTagIds: {
            get() {
                return (this.activeClient.tags || []).map(t => t.id);
            },
            set(val) {
                this.updateField(val, "tag_ids");
                const selectedTags = (this.tags || []).filter(t => val.includes(t.id));
                this.updateField(selectedTags, "tags");
            }
        }
    },
    methods: {
        updateField(value, field) {
            Store.commit("ClientStore/updateActiveClientProperty", { prop: field, value: value });
        },
        addContact() {
            const contacts = [...(this.activeClient.contacts || [])];
            contacts.push({ name: null, position: null, phone: null, email: null, comment: null });
            this.updateField(contacts, "contacts");
        },
        removeContact(index) {
            const contacts = [...(this.activeClient.contacts || [])];
            contacts.splice(index, 1);
            this.updateField(contacts, "contacts");
        },
        updateContact(index, field, value) {
            const contacts = (this.activeClient.contacts || []).map((c, i) =>
                i === index ? { ...c, [field]: value } : c
            );
            this.updateField(contacts, "contacts");
        }
    }
};
</script>

<style scoped>
.edit-content {
    padding: 12px 20px;
}

.edit-row {
    display: flex;
    align-items: center;
    min-height: 32px;
    margin-bottom: 4px;
}

.row-title {
    color: #888;
    width: 140px;
    flex-shrink: 0;
    font-size: 13px;
}

.row-value-readonly {
    font-size: 13px;
    color: #333;
}

.required {
    color: #f41823;
}

.compact-input {
    flex: 1;
    max-width: 300px;
}

.edit-row.align-start {
    align-items: flex-start;
}

.contacts-wrap {
    flex: 1;
    max-width: 300px;
}

::v-deep .compact-input .v-input__slot {
    min-height: 28px !important;
    padding: 0 8px !important;
}

::v-deep .compact-input input {
    padding: 4px 0 !important;
    font-size: 13px;
}

::v-deep .compact-input .v-label {
    font-size: 13px;
    top: 50%;
    transform: translateY(-50%);
}

::v-deep .compact-input .v-label--active {
    top: 0;
    transform: translateY(-50%) scale(0.85);
}

::v-deep .compact-input .v-select__selections {
    padding: 0 !important;
    min-height: 28px !important;
    font-size: 13px;
}

::v-deep .compact-input .v-select__selections input {
    padding: 4px 0 !important;
}

::v-deep .compact-input .v-input__prepend-inner,
::v-deep .compact-input .v-input__append-inner {
    margin-top: 4px !important;
}

.date-picker-wrap ::v-deep input {
    width: 100%;
    height: 30px;
    padding: 4px 8px;
    font-size: 13px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    outline: none;
}

.date-picker-wrap ::v-deep input:focus {
    border-color: currentColor;
}
</style>
